import { useEffect, useMemo } from 'react';
import { Divider, Stack, Toolbar } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash';

import { DomainId } from '@inspiren-monorepo/util-rooms';

import DatePicker from './DatePicker';
import { AdminViewToggle } from './EventToolbar/AdminViewToggle';
import { getOrgs } from './data-access/getOrgs';
import { getRoomsForUnit } from './data-access/getRoomsForUnit';
import { getUnits } from './data-access/getUnits';
import { useUpdateSelection } from './hooks/useUpdateSelection';
import { useUpdateUrlParams } from './hooks/useUpdateURLParams';
import { useEventReviewStoreShallow } from './store/EventReviewStore';

import { useCurrentUser } from '../../HOC/CurrentUserContextProvider';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { sendAmpEvent } from '../../utility/amplitude';
import { sortUnitDropdown } from '../../utility/helpers/sortUnitDropdown';
import LoadingAutocomplete from '../shared/LoadingAutocomplete';

const EventToolbar = () => {
  const {
    selectedOrg,
    selectedUnit,
    selectedRoom,
    startDate,
    endDate,
    setSelectedOrg,
  } = useEventReviewStoreShallow([
    'selectedOrg',
    'selectedUnit',
    'selectedRoom',
    'startDate',
    'endDate',
    'setSelectedOrg',
  ]);

  const { updateSelectedOrg, updateSelectedUnit, updateSelectedRoom } =
    useUpdateSelection();

  const { user } = useCurrentUser();
  const { isAdmin, isAdminLoading } = useIsAdmin();
  const levelAccess = user?.levelAccess;
  const awsOrg = import.meta.env.VITE_ORG_ID;

  const crossOrgAccess = isAdmin && !levelAccess;

  const { isLoading: orgsLoading, data: orgs = [] } = useQuery({
    queryKey: ['eventReview', 'orgs'],
    queryFn: getOrgs,
  });

  const { isLoading: unitsLoading, data: units } = useQuery({
    queryKey: ['eventReview', 'units'],
    queryFn: getUnits,
  });

  const unitId = selectedUnit && selectedUnit.domainId;

  const { isFetching: roomsLoading, data: rooms } = useQuery({
    queryKey: ['eventReview', 'rooms', unitId],
    queryFn: () => (unitId ? getRoomsForUnit(unitId) : Promise.resolve([])),
    enabled: unitId !== undefined,
  });

  // auto-select the aws org
  useEffect(() => {
    if (orgs?.length && awsOrg && !orgsLoading && !isAdminLoading) {
      const orgObj = orgs.find((o) => o.id === awsOrg);
      if (orgObj) setSelectedOrg(orgObj);
    }
  }, [orgs, awsOrg, orgsLoading, isAdminLoading]);

  useEffect(() => {
    if (selectedRoom && startDate && endDate) {
      sendAmpEvent('Date Selection', {
        room: selectedRoom.domainId,
        start: startDate,
        end: endDate,
      });
    }
  }, [selectedRoom, startDate, endDate]);

  const filteredUnits = useMemo(
    () =>
      selectedOrg
        ? sortUnitDropdown(
            units?.filter(
              (unit) =>
                unit.domainId.includes(`${selectedOrg.id}-`) && !unit.hide,
            ),
          )
        : [],
    [selectedOrg, units],
  );

  const filteredRooms = useMemo(
    () =>
      selectedUnit && rooms
        ? rooms?.sort((a, b) => a.displayName.localeCompare(b.displayName))
        : [],
    [selectedUnit, rooms],
  );

  useUpdateUrlParams({
    orgs,
    units,
    filteredRooms,
    orgsLoading,
    unitsLoading,
    roomsLoading,
  });

  return (
    <Toolbar>
      <Stack
        direction='row'
        gap={2}
        flexWrap='wrap'
        justifyContent='center'
        alignItems='center'
        width='100%'
        p={2}
      >
        {crossOrgAccess && (
          <LoadingAutocomplete
            label='Organization'
            loading={orgsLoading}
            size='small'
            id='org'
            data-testid='org'
            options={sortBy(orgs, 'name')}
            value={selectedOrg}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_e, newValue) => updateSelectedOrg(newValue)}
            sx={{ minWidth: 200 }}
          />
        )}

        <LoadingAutocomplete
          label='Unit'
          loading={
            crossOrgAccess
              ? unitsLoading && Boolean(selectedOrg)
              : unitsLoading || orgsLoading
          }
          size='small'
          id='unit'
          data-testid='unit'
          options={filteredUnits}
          getOptionLabel={(option) =>
            `${option.buildingDisplayName || option.address} ${
              option.displayName || option.name
            }`
          }
          renderOption={(props, option) => (
            <li {...props}>{option.displayName || option.name}</li>
          )}
          groupBy={(option) => option.buildingDisplayName || option.address}
          value={selectedUnit}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(_e, newValue) => updateSelectedUnit(newValue)}
          disabled={crossOrgAccess && !selectedOrg}
          sx={{ minWidth: 285 }}
        />

        <LoadingAutocomplete
          label='Room'
          loading={roomsLoading && Boolean(selectedUnit)}
          size='small'
          id='room'
          data-testid='room'
          options={filteredRooms}
          getOptionLabel={(option) =>
            option.displayName || DomainId.parse(option.domainId).room || ''
          }
          value={selectedRoom}
          isOptionEqualToValue={(o, v) => o.domainId === v.domainId}
          onChange={(_e, newValue) => updateSelectedRoom(newValue)}
          disabled={!selectedOrg || !selectedUnit}
          sx={{ minWidth: 180 }}
        />

        <Divider
          sx={{
            width: '100%',
            border: 'none',
            display: { xs: 'block', sm: 'none' },
          }}
        />

        <DatePicker />

        {isAdmin && <AdminViewToggle />}
      </Stack>
    </Toolbar>
  );
};

export default EventToolbar;
